import cloneDeep from 'lodash.clonedeep';
import merge from 'lodash.merge';
import { Feedback } from '../../types/feedback';
import { uuid } from '../../types/common-helper';
import { Resource } from '../../types/game-document';
import {
  ResourceEntity,
  ResourcePackEntity
} from '../../types/game-document/entities';
import {
  LanguageMapping,
  ResourcePack
} from '../../types/game-document/entities/resource-pack';
import { GetNextAssetNameAsync } from '../../utils/game-document/assets';
import {
  TranslateUsingOpenAIAsync,
  TranslationProgressEvent
} from '../../services/translation';

/**
 * Retrieves all resources the game document.
 * @param feedbackDocument - The Feedback Document to modify
 */
export const GetResources = (feedbackDocument: Feedback): ResourceEntity[] => {
  return (feedbackDocument.resources as ResourceEntity[]) ?? [];
};

/**
 * Retrieves resource by ID from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourceId - The Resource ID to be obtained
 */
export const GetResourceById = (
  feedbackDocument: Feedback,
  resourceId: string
) => feedbackDocument.resources?.find((resource) => resource.id === resourceId);

/**
 * Retrieves all resource packs from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 */
export const GetResourcePacks = (
  feedbackDocument: Feedback
): ResourcePackEntity[] => {
  return (feedbackDocument.resourcePacks as ResourcePackEntity[]) ?? [];
};

/**
 * Retrieves resource from resource pack's resource by ID from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourceId - The Resource ID to be obtained
 */
export const GetResourcePackResourcesById = (
  feedbackDocument: Feedback,
  resourcePackId: string
): ResourceEntity[] => {
  let selectedResourcePack: ResourcePackEntity =
    feedbackDocument.resourcePacks?.find(
      (resourcePack) => resourcePack.id === resourcePackId
    );
  return (selectedResourcePack.resources as ResourceEntity[]) ?? [];
};

/**
 * Retrieves resource pack by name from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param name - The Resource Pack's name to be obtained
 */
export const GetResourcePackByName = (
  feedbackDocument: Feedback,
  name: string
) => {
  const resources = cloneDeep(feedbackDocument?.resources);
  let newLanguageMapping: LanguageMapping[] = [];
  const selectedResourcePack: ResourcePack =
    feedbackDocument?.resourcePacks?.find((x) => x.name === name);

  resources?.forEach((resource) => {
    let selectedNewLanguage = selectedResourcePack?.resources?.find(
      (x) => x.id === resource?.id
    );

    newLanguageMapping.push({
      id: resource?.id,
      type: resource.type,
      originalLanguage: resource?.value,
      newLanguage: selectedNewLanguage && selectedNewLanguage?.value,
      isEditing: true
    });
  });

  return newLanguageMapping;
};

/**
 * Retrieves a resource entity from the Feedback Document by id.
 * @param feedbackDocument - The Feedback Document to check resources
 * @param resourceId - The ID of the Resource to update
 */
export const GetResourceEntity = (
  feedbackDocument: Feedback,
  resourceId: string
): ResourceEntity => {
  let resources = GetResources(feedbackDocument);
  let resourceIndex = resources.findIndex((i) => i.id === resourceId)!;
  return resources[resourceIndex];
};

/**
 * Retrieves a resource value from the Feedback Document by id.
 * @param feedbackDocument - The Feedback Document to check resources
 * @param resourceId - The ID of the Resource to update
 */
export const GetResourceValue = (
  feedbackDocument: Feedback,
  resourceId: string
) => {
  let resourceEntity = GetResourceEntity(feedbackDocument, resourceId);
  return resourceEntity?.value ?? '';
};

export const MergeResources = (
  feedbackDocument: Feedback,
  resources: Array<ResourceEntity>
) =>
  merge(feedbackDocument, {
    resources
  });

export const GetNextResourceNameAsync = async (
  resources: Array<ResourceEntity>,
  name: string,
  id?: string
) => {
  let resourceExists = await ResourceExistsAsync(resources, name, id);
  let counter = 1;
  let nextResourceName = name;
  while (resourceExists) {
    nextResourceName = `${name}-${counter}`;
    resourceExists = await ResourceExistsAsync(resources, nextResourceName, id);
    counter += 1;
  }
  return nextResourceName;
};

export const ResourceExistsAsync = async (
  resources: Array<ResourceEntity>,
  name: string,
  id?: string
) => {
  let resourceIndex = resources.findIndex(
    (i) => i.name === name && i.id !== id
  );
  return resourceIndex !== -1;
};

/**
 * Retrieve resource entity from resource pack's resource from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param name - The Resource Pack name
 * @param resourceId - Resource ID to be obtained
 */
export const GetResourcePackResource = (
  feedbackDocument: Feedback,
  name: string,
  resourceId: string
) => {
  const selectedResourcePack: ResourcePack =
    feedbackDocument?.resourcePacks?.find((x) => x.name === name);

  return selectedResourcePack?.resources?.find((x) => x.id === resourceId);
};

/**
 * Add new resource to the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param entity - New resource entity
 */
export const AddResourceEntityAsync = async (
  feedbackDocument: Feedback,
  entity: ResourceEntity
) => {
  let resources = GetResources(feedbackDocument);
  let resourceName = await GetNextResourceNameAsync(resources, entity.name);
  resources.push({ ...entity, name: resourceName });
  return MergeResources(feedbackDocument, resources);
};

/**
 * Update existing resource from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourceId - Resource ID to be updated
 * @param resource - Updated resource entity
 */
export const UpdateResourceAsync = async (
  feedbackDocument: Feedback,
  resourceId: string,
  resource: ResourceEntity
) => {
  let resources = GetResources(feedbackDocument);
  let resourceIndex = resources.findIndex((i) => i.id === resourceId)!;
  resource.name = await GetNextResourceNameAsync(
    resources,
    resource.name,
    resource.id
  );
  resources[resourceIndex] = resource;
  return MergeResources(feedbackDocument, resources);
};

/**
 * Delete existing resource from the game document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourceId - Resource ID to be deleted
 */
export const DeleteResourceAsync = async (
  feedbackDocument: Feedback,
  resourceId: string
) => {
  let resources = GetResources(feedbackDocument);
  let resourceIndex = resources.findIndex((i) => i.id === resourceId)!;
  if (resourceIndex < 0) {
    return feedbackDocument;
  }
  resources.splice(resourceIndex, 1);
  return MergeResources(feedbackDocument, resources);
};

/**
 * Add new resource pack from the Feedback document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param name - The name of the Resource Pack
 * @param isCopyOriginaltext - Trigger to copy the original resource's values
 * @param displayLanguage - name of display language selected
 * @param displayLanguageUrl - blob file url of display language selected
 * @return The updated Feedback Document
 */
export const AddResourcePackAsync = async (
  feedbackDocument: Feedback,
  name: string,
  isCopyOriginaltext?: boolean,
  isTranslateUsingAI?: boolean,
  displayLanguage?: string,
  displayLanguageUrl?: string,
  onProgress?: (event: TranslationProgressEvent) => void
) => {
  let resourcePacks = cloneDeep(feedbackDocument.resourcePacks) ?? [];
  let resourcePackName = await GetNextAssetNameAsync(resourcePacks, name);

  let originalResources: Resource[] =
    cloneDeep(feedbackDocument?.resources) ?? [];

  let newResourcePack: ResourcePackEntity = {
    id: uuid(),
    description: '',
    name: resourcePackName,
    displayLanguage,
    displayLanguageUrl,
    resources: []
  };

  if (isTranslateUsingAI) {
    // format the resource list to follow the API requirement to done the translation
    const resourceMap: Record<string, string> = originalResources.reduce(
      (acc, resource) => {
        if (resource.id !== undefined) {
          acc[resource.id] = resource.value;
        }
        return acc;
      },
      {} as Record<string, string>
    );

    // send the resources to the API for translation
    var translatedResources = await TranslateUsingOpenAIAsync(
      name,
      resourceMap,
      onProgress
    );

    // set the resources of the resource pack
    originalResources.forEach((resource) => {
      resource.value = translatedResources[resource.id!];
      newResourcePack.resources!.push(resource as ResourceEntity);
    });
  } else {
    originalResources.forEach((resource) => {
      if (!isCopyOriginaltext) {
        resource.value = '';
      }
      newResourcePack.resources!.push(resource as ResourceEntity);
    });
  }

  resourcePacks.push(newResourcePack);

  return merge(feedbackDocument, {
    resourcePacks
  });
};

/**
 * Updates the resources pack detail in the Feedback.
 * Not Update resources of resource pack.
 * @param Assessment - The Feedback to modify
 * @param resourcePackId - The ID of the Resource pack to update
 * @param displayLanguage - name of display language selected
 * @param displayLanguageUrl - blob file url of display language selected
 * @return The updated Feedback Document
 */
export const UpdateFeedbackResourcePackDetail = async (
  feedbackDocument: Feedback,
  name: string,
  resourcePackId: string,
  displayLanguage: string,
  displayLanguageUrl: string
) => {
  let resourcePacks = GetResourcePacks(feedbackDocument);

  let resourcePacksIndex = resourcePacks.findIndex(
    (i) => i.id === resourcePackId
  )!;
  if (resourcePacksIndex > -1) {
    let newResourcePack = resourcePacks[resourcePacksIndex];
    newResourcePack.name = name;
    newResourcePack.displayLanguage = displayLanguage;
    newResourcePack.displayLanguageUrl = displayLanguageUrl;

    resourcePacks[resourcePacksIndex] = newResourcePack;
  }
  return merge(feedbackDocument, {
    resourcePacks
  });
};

/**
 * Updates the resource pack's resource in the Feedback Document.
 * @param gameDocument - The Feedback Document to modify
 * @param resourceId - The ID of the Resource to update
 * @param resource - The updated Resource
 */
export const UpdateResourcePackResourceAsync = async (
  feedbackDocument: Feedback,
  name: string,
  resourceId: string,
  resource: ResourceEntity
) => {
  let resourcePacks = GetResourcePacks(feedbackDocument);

  let resourcePacksIndex = resourcePacks.findIndex((i) => i.name === name)!;

  if (resourcePacksIndex > -1) {
    let resourcePack = resourcePacks[resourcePacksIndex];

    let resourceIndex = resourcePack?.resources?.findIndex(
      (x) => x.id === resourceId
    )!;

    if (resourceIndex > -1) {
      resourcePacks[resourcePacksIndex].resources![resourceIndex] = resource;
    }

    return merge(feedbackDocument, {
      resourcePacks
    });
  }
};

/**
 * Delete a resource inside resource pack's resource in the Feedback Document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourcePackResourceId - The ID of the Resource to delete
 */
export const DeleteResourcePackResourceAsync = async (
  feedbackDocument: Feedback,
  resourcePackResourceId: string
) => {
  let resourcePacks = GetResourcePacks(feedbackDocument);

  if (resourcePacks.length > 0) {
    resourcePacks.forEach((resPack) => {
      let resourcePackResourceIndex = resPack.resources!.findIndex(
        (i) => i.id === resourcePackResourceId
      )!;
      if (resourcePackResourceIndex > -1) {
        resPack.resources!.splice(resourcePackResourceIndex, 1);
      }
    });

    return merge(feedbackDocument, {
      resourcePacks
    });
  } else {
    return feedbackDocument;
  }
};

/**
 * Add the resource pack's resource in the Feedback Document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param resourceId - The ID of the Resource to update
 * @param resource - The updated Resource
 */
export const AddResourcePackResourceAsync = async (
  feedbackDocument: Feedback,
  name: string,
  resource: ResourceEntity
) => {
  let resourcePacks = GetResourcePacks(feedbackDocument);

  let resourcePacksIndex = resourcePacks.findIndex((i) => i.name === name)!;

  if (resourcePacksIndex > -1) {
    resourcePacks[resourcePacksIndex].resources?.push(resource);

    return merge(feedbackDocument, {
      resourcePacks
    });
  }
};

/**
 * Add the resource pack's resource in the Feedback Document.
 * @param feedbackDocument - The Feedback Document to modify
 * @param originalResourceId - The original ID of the Resource to be searched
 * @param newResourceId - The new ID of the copied Resource
 */
export const copyResourceAndPackById = async (
  feedbackDocument: Feedback,
  originalResourceId: string,
  newResourceId: string
) => {
  let newDocument = cloneDeep(feedbackDocument);
  let newResource = cloneDeep(GetResourceById(newDocument, originalResourceId));

  if (newResource !== undefined) {
    newResource.id = newResourceId;

    await AddResourceEntityAsync(newDocument, newResource).then((response) => {
      if (
        response.resourcePacks !== undefined &&
        response.resourcePacks.length > 0
      ) {
        let newResourcePack: ResourceEntity | undefined;

        response.resourcePacks.forEach((resPack) => {
          newResourcePack = cloneDeep(
            GetResourcePackResourcesById(newDocument, resPack.id).find(
              (x) => x.id === originalResourceId
            )
          );
          if (newResourcePack !== undefined) {
            newResourcePack.id = newResourceId;
            AddResourcePackResourceAsync(
              newDocument,
              resPack.name,
              newResourcePack
            ).then((response) => {
              if (response !== undefined) {
                newDocument = response;
              }
            });
          } else {
            return;
          }
        });

        return newDocument;
      } else {
        return response;
      }
    });

    return newDocument;
  } else {
    return newDocument;
  }
};
